import React from 'react';
import {
    Select,
    MenuItem,
    ListSubheader,
    Checkbox,
    Typography,
    Box,
} from '@mui/material';

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';

/**
 * A multi-select checkbox dropdown grouped under headings — the class and
 * grade pickers of the My Classes deck (30 Sep 2026, A4, A7, C2, C3).
 *
 *   options: [{ value, label, group?, meta?, disabled?, disabledLabel? }]
 *
 * `meta` is the small grey text on the right ("24 learners"); a disabled
 * option shows `disabledLabel` there instead ("Published"). The closed state
 * shows the selected labels as plain text.
 */
const GroupedMultiSelect = ({
    options = [],
    value = [],
    onChange,
    placeholder = 'Select',
    size = 'small',
    fullWidth = true,
    disabled = false,
    renderSelected,
    sx,
}) => {
    const byValue = new Map(options.map((o) => [String(o.value), o]));

    // Group headings in first-seen order — the caller decides the order.
    const groups = [];
    const seen = new Map();
    for (const o of options) {
        const g = o.group || '';
        if (!seen.has(g)) {
            seen.set(g, []);
            groups.push(g);
        }
        seen.get(g).push(o);
    }

    const items = [];
    for (const g of groups) {
        if (g) {
            items.push(
                <ListSubheader
                    key={`h:${g}`}
                    sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', lineHeight: '30px', color: MUTED, textTransform: 'uppercase' }}
                >
                    {g}
                </ListSubheader>,
            );
        }
        for (const o of seen.get(g)) {
            const checked = value.map(String).includes(String(o.value));
            items.push(
                <MenuItem key={String(o.value)} value={String(o.value)} disabled={!!o.disabled} dense sx={{ py: 0.25 }}>
                    <Checkbox
                        size="small"
                        checked={checked}
                        disabled={!!o.disabled}
                        sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: ORANGE } }}
                    />
                    <Typography sx={{ fontSize: '13.5px', flex: 1 }}>{o.label}</Typography>
                    {(o.disabled ? o.disabledLabel : o.meta) && (
                        <Typography sx={{ fontSize: '12px', color: MUTED, ml: 2 }}>
                            {o.disabled ? o.disabledLabel : o.meta}
                        </Typography>
                    )}
                </MenuItem>,
            );
        }
    }

    return (
        <Select
            multiple
            displayEmpty
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            value={value.map(String)}
            onChange={(e) => {
                const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                // A click on a group heading reaches here as `undefined`.
                onChange(next.filter((v) => v !== undefined && byValue.has(String(v))));
            }}
            renderValue={(selected) => {
                if (!selected.length) {
                    return <Box component="span" sx={{ color: MUTED }}>{placeholder}</Box>;
                }
                if (renderSelected) return renderSelected(selected);
                return selected.map((v) => byValue.get(String(v))?.label || v).join(', ');
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 340 } } }}
            sx={{ fontSize: '13.5px', backgroundColor: 'var(--brand-surface, #fff)', ...sx }}
        >
            {items}
        </Select>
    );
};

export default GroupedMultiSelect;
